import { createContext } from 'react';

import _ from 'lodash';
import { getIntl, history } from 'umi';

import { bindServices } from '@/services/api';
import { formFieldBehaviorsMapping } from '@/utils/core/business-logic';
import { CoreEnums } from '@/utils/domain/constants';
import { toPaginate } from '@/utils/infrastructure';
import { Regexp } from '@/utils/infrastructure/utilities';
import CoreQueries from '@/utils/input/queries';
import { normalizeActionTypes } from './actionTypeFilter';
import {
  commandMessageType,
  commandResponseMessage,
  filtersConfig,
  iterationResultsFiltersConfig,
} from './utils';

// Bind API services to namespace
const api = bindServices('applications');

export const RequestContext = createContext({});

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const notices = {
  approve: false,
  reject: false,
  complete: false,
  executeTask: false,
  sign: false,
  cancel: false,
  void: false,
  sendNotifications: false,
  evaluateApprovers: false,
  backToStep: false,
  reassignTask: false,
  executeAutomaticAction: false,
  history: false,
  continue: false,
  autoAssignTask: false,
};

const resolveActionInstanceId = (instance) =>
  instance?.currentActionInstanceId ||
  instance?.currentActionInstance?.id ||
  _.last(instance?.actionInstances)?.id;

/**
 * A step command on the external detail can be read before the engine
 * advances. Keep the locked snapshot unless this payload already shows that
 * move (new step, new action status, or new request status).
 */
const instanceDetailReflectsCommand = (currentItem, instance) => {
  if (!currentItem) return true;
  if (!instance) return false;

  const previousId = currentItem.currentActionInstanceId;
  const nextId = resolveActionInstanceId(instance);
  if (!previousId) return true;
  if (!nextId) return false;
  if (previousId !== nextId) return true;
  if (!_.isNil(instance.status) && instance.status !== currentItem.status) return true;

  const previousActionStatus = currentItem.currentActionInstance?.status;
  const nextActionStatus = instance.currentActionInstance?.status;
  if (!_.isNil(nextActionStatus) && nextActionStatus !== previousActionStatus) return true;

  return false;
};

const initialState = {
  list: [],
  autoOpenEditOperation: false,
  currentItem: null,
  selectItem: null,
  pagination: meta,
  actionTypeResultPagination: meta,
  emailEventTraceResultPagination: { ...meta, limit: 10 },
  completeFormConfig: null,
  editTableRowVisible: false,
  tableRecord: null,
  continueValidationErrors: [],
  instanceActionType: null,
  customActiveTab: null,
  executedCommand: null,
  // External approve/reject: getDetails still showed the pre-command step.
  commandSyncPending: false,
  currentExecutedActionInstanceId: null,
  stepStatusChanged: null,
  filtersConfig,
  iterationResultsFiltersConfig,
  // bulk request item
  bulkTaskItemData: [],
  bulkTaskItemPagination: meta,
  // email event trace
  emailEventTrace: undefined,
  ...notices,
};

const Model = {
  namespace: 'applications',
  state: initialState,
  effects: {
    *fetchList({ payload }, { call, put }) {
      try {
        let res = null;
        const { reqOrigin, applicationStatus, page, limit, actionTypes, ...params } = payload;
        const actionTypesParam = normalizeActionTypes(actionTypes);

        switch (reqOrigin) {
          case CoreEnums.ProcessInstanceRequestOrigin.All:
          case CoreEnums.ProcessInstanceRequestOrigin.MyRequests:
          case CoreEnums.ProcessInstanceRequestOrigin.ReceivedRequests:
          case CoreEnums.ProcessInstanceRequestOrigin.RevisionRequests:
            const fragments = [reqOrigin, applicationStatus].filter((v) => !_.isNil(v)).join('/');
            res = yield call(api.getAll, {
              route: `processInstance/requests/${fragments}`,
              page,
              limit,
              ...CoreQueries.Requests.List,
              ...params,
              // toQuery drops unknown keys. passthroughParams keeps repeated
              // `actionTypes` keys (umi-request arrayFormat: 'repeat').
              ...(actionTypesParam && { passthroughParams: { actionTypes: actionTypesParam } }),
            });
            break;
          default:
            res = yield call(api.getAll, {
              route: 'bulkRequestTask/paged',
              page,
              limit,
              ...CoreQueries.Requests.BulkUpload,
              ...params,
            });
            break;
        }

        yield put({
          type: 'queryApplications',
          payload: { ...res, page, limit },
        });
      } catch (e) {}
    },

    *fetchById({ payload }, { call, put, select }) {
      const {
        processInstanceId,
        actionInstanceId,
        versionHistoryMatch,
        confirmAfterCommand,
      } = payload;
      // Keep this flag off the request. getAll forwards unknown payload fields.
      const requestPayload = _.omit(payload, ['confirmAfterCommand']);
      try {
        const endPoint = versionHistoryMatch
          ? `history/${processInstanceId}/${actionInstanceId}`
          : `getDetails/${processInstanceId}`;

        const data = yield call(api.getAll, {
          route: `processInstance/${endPoint}`,
          ...requestPayload,
        });

        if (confirmAfterCommand) {
          const { currentItem, completeFormConfig } = yield select((state) => state.applications);
          const instance = data?.instance;
          const formInstance = completeFormConfig?.processInstance;
          const baseline =
            currentItem?.id && currentItem.id === instance?.id
              ? currentItem
              : formInstance?.id && formInstance.id === instance?.id
              ? {
                  ...formInstance,
                  currentActionInstanceId:
                    formInstance.currentActionInstanceId || completeFormConfig.actionInstanceId,
                }
              : currentItem;
          if (!instanceDetailReflectsCommand(baseline, instance)) {
            yield put({ type: 'queryCommandSyncPending' });
            return;
          }
        }

        const autoOpen = yield select((o) => o.applications.autoOpenEditOperation);

        yield put({
          type: 'queryById',
          payload: {
            data,
            autoOpenEditOperation: !!(
              data?.process?.autoOpenEditOperation &&
              (autoOpen || Boolean(+sessionStorage.getItem('x-autoOpenEditOperation')))
            ),
          },
        });
      } catch (e) {
        if (e?.aborted) return;
        // A failed confirmation read must not leave the external page.
        // The socket path finishes the sync, and the approve action stays locked.
        if (confirmAfterCommand) {
          yield put({ type: 'queryCommandSyncPending' });
          return;
        }
        const route = _.split(history.location.pathname, '/')
          .filter((str) => !Regexp.isUUID(str))
          .join('/');

        history.replace(route);
      }
    },

    *fetchBulkTaskById({ payload }, { call, put }) {
      try {
        const data = yield call(api.getById, {
          route: 'bulkRequestTask',
          ...payload,
          ...CoreQueries.Requests.BulkTaskDetail,
        });
        yield put({ type: 'queryById', payload: { data, bulk: true } });
      } catch (e) {
        if (!(e instanceof Response)) return;
        if (!history.location.pathname.includes('bulkRequestTask')) return;
        history.replace(
          `/applications?requestOrigin=${CoreEnums.ProcessInstanceRequestOrigin.BulkRequests}`,
        );
      }
    },

    *fetchBulkTaskItem({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, {
          route: 'bulkRequestTaskItem/paged',
          ...payload,
          ...CoreQueries.Requests.BulkTaskPaged,
        });
        yield put({
          type: 'queryBulkTaskItem',
          payload: {
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *instanceStatusChanged({ payload }, { put }) {
      yield put({ type: 'queryInstanceStatusChanged', payload });
    },

    *download({ payload }, { call }) {
      try {
        const res = yield call(api.getById, {
          route: `formInstanceFieldAttachment/download`,
          ...payload,
        });
        const blob = new Blob([res], { type: 'application/octetstream' });
        const url = window.URL || window.webkitURL;
        const link = url.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('download', payload.fileName);
        a.setAttribute('href', link);
        a.click();
        url.revokeObjectURL(link);
      } catch (e) {}
    },

    *exeCommand({ payload }, { call, put, select }) {
      let success = false;
      try {
        const { formatMessage } = getIntl();

        const messageType = commandMessageType[payload.command] || 'info';
        const { name } = yield select((state) => state.applications.currentItem);

        yield call(api.exec, payload);
        yield put({ type: 'queryExec', payload: { command: payload.command } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: messageType,
            ...(messageType && {
              message: formatMessage(
                { id: 'applications.text.message.success' },
                {
                  name,
                  action: formatMessage({ id: commandResponseMessage[payload.command] }),
                },
              ),
            }),
          },
        });
        success = true;
      } catch (e) {
      } finally {
        // Pass success so UI can keep action buttons locked until the step advances.
        payload.callback?.(success);
      }
    },

    *fetchCompleteInfo({ payload }, { call, put }) {
      try {
        const res = yield call(api.exec, payload);
        yield put({ type: 'queryComplete', payload: res });
      } catch (e) {
        payload.callback?.();
      }
    },

    *complete({ payload }, { call, put }) {
      try {
        const fragments = [
          payload?.processInstanceId,
          payload?.actionInstanceId,
          payload?.formInstanceId,
          payload?.operationType,
        ].filter(Boolean);

        yield call(api.create, {
          route: `processEngine/continue/${fragments.join('/')}`,
          ..._.omit(payload, [
            'processInstanceId',
            'actionInstanceId',
            'formInstanceId',
            'operationType',
          ]),
        });

        yield put({
          type: 'queryExec',
          payload: {
            ..._.pick(payload, ['autoOpenEditOperation', 'actionInstanceId']),
            command: CoreEnums.RequestDetailCommandType.Continue,
            complete: true,
          },
        });
        // Detail → continue skips tabs cleanup, so Forms cache would keep pre-save
        // values (autocalculado stale until full refresh). Force a fresh fetch.
        yield put({ type: 'applicationTabs/invalidateTab', payload: { tab: 'forms' } });
        yield put({ type: 'applicationTabs/invalidateTab', payload: { tab: 'variables' } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'info' },
        });
      } catch (e) {
        if (e?.validationErrors) {
          yield put({ type: 'queryCompleteError', payload: e });
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }
      }
    },

    *fetchEmailEventTrace({ payload }, { call, put }) {
      try {
        const res = yield call(api.getData, payload);
        yield put({ type: 'queryEventTrace', payload: res });
      } catch (e) {}
    },

    *customActiveTab({ payload }, { put }) {
      yield put({ type: 'queryCustomActiveTab', payload });
    },

    *consumeSequence({ payload }, { call, put }) {
      try {
        const res = yield call(api.getData, payload);
        // yield put({ type: '', payload: res });
      } catch (e) {}
    },

    *instanceActionType({ payload }, { put }) {
      yield put({ type: 'queryInstanceActionType', payload });
      if (!payload) {
        const initial = { items: [], ...meta };
        yield put({ type: 'queryEmailEventTraceResult', payload: initial });
      }
    },

    *actionTypeResult({ payload }, { call, put }) {
      try {
        const res = yield call(api.getAll, payload);
        // Normalize shape so reducer can distinguish list vs single item
        const normalized = {
          totalCount: 0,
          ...(_.isArray(res?.items) ? res : {}),
          ...(!_.isArray(res?.items) ? { item: res } : {}),
          ..._.pick(payload, ['page', 'limit', 'source']),
        };
        yield put({ type: 'queryActionTypeResult', payload: normalized });
      } catch (e) {}
    },

    *emailEventTraceResult({ payload }, { call, put }) {
      try {
        const {
          processInstanceId,
          actionInstanceId,
          emailMessageTraceId,
          page,
          limit,
          orderBy = 'created asc',
        } = payload;

        const pathSegments = [processInstanceId, actionInstanceId].filter(Boolean);
        let route = `processInstance/data/emailTrackingEvents/${pathSegments.join('/')}`;
        if (emailMessageTraceId) {
          route += `?emailMessageTraceId=${encodeURIComponent(emailMessageTraceId)}`;
        }

        const res = yield call(api.getAll, {
          route,
          page,
          limit,
          orderBy,
        });

        yield put({
          type: 'queryEmailEventTraceResult',
          payload: {
            totalCount: 0,
            ...res,
            ..._.pick(payload, ['page', 'limit']),
          },
        });
      } catch (e) {}
    },

    *toggleEditTableRowVisible({ payload }, { put }) {
      yield put({ type: 'queryEditTableRowVisible', payload });
    },

    *toggleAutoOpenEditEnabled({ payload }, { put }) {
      yield put({ type: 'queryAutoOpenEditEnabled', payload });
    },

    *cleanupCurrentItem(o, { put }) {
      yield put({ type: 'cleanupCurrent' });
    },

    *cleanupSelectItem(o, { put }) {
      yield put({ type: 'cleanupSelect' });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'queryCleanup', payload });
    },
  },
  reducers: {
    queryApplications(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        executedCommand: null,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryById(state, { payload }) {
      const { instance, process, versionId, ...bulkData } = payload.data || {};
      const currentActionInstanceId =
        instance?.currentActionInstanceId ||
        instance?.currentActionInstance?.id ||
        _.last(instance?.actionInstances)?.id;
      return {
        ...state,
        executedCommand: null,
        commandSyncPending: false,
        autoOpenEditOperation: payload.autoOpenEditOperation,
        currentItem: {
          ...instance,
          process,
          versionId,
          ...(payload.bulk && { ...bulkData }),
          commands: _.orderBy(instance?.commands, ['customCommandType'], ['asc']),
          currentActionInstanceId,
          // Tag commands with the step they belong to (avoids cross-step flashes).
          commandsActionInstanceId: currentActionInstanceId || null,
        },
      };
    },

    queryBulkTaskItem(state, { payload }) {
      return {
        ...state,
        bulkTaskItemData: payload.items,
        bulkTaskItemPagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    queryExec(state, { payload }) {
      return {
        ...state,
        complete: !!payload.complete,
        executedCommand: payload.command,
        autoOpenEditOperation: payload.autoOpenEditOperation,
        ...(payload.actionInstanceId && {
          currentExecutedActionInstanceId: payload.actionInstanceId,
        }),
      };
    },

    queryExecError(state, { payload }) {
      return {
        ...state,
        continueValidationErrors: payload,
      };
    },

    queryComplete(state, { payload }) {
      return {
        ...state,
        autoOpenEditOperation: false,
        currentExecutedActionInstanceId: null,
        completeFormConfig: {
          ...payload,
          behaviorCases: formFieldBehaviorsMapping(payload),
        },
      };
    },

    queryCompleteError(state, { payload }) {
      return {
        ...state,
        continueValidationErrors: payload?.validationErrors || [],
      };
    },

    queryEditTableRowVisible(state, { payload }) {
      return {
        ...state,
        editTableRowVisible: payload?.state ?? false,
        tableRecord: payload?.record ?? null,
      };
    },

    queryInstanceStatusChanged(state, { payload }) {
      if (!payload || Object.keys(payload).length === 0) {
        return state;
      }

      const actionInstances = [...(state.currentItem?.actionInstances || [])];

      const {
        approvers,
        stepStatus,
        iterationResultId,
        actionInstanceStatus,
        processInstanceStatus,
        currentActionInstanceId,
        currentActionInstanceStatus,
        previousActionInstance,
        currentActionInstance,
        actionInstanceId,
        status,
        markPreviousCompleted,
        holdVisibleStep,
      } = payload || {};

      // The engine moved on to a step this role cannot see (automatic work).
      // Close the visible step they were on and leave the timeline there.
      // Inserting the hidden step makes a card appear and the next getDetails
      // removes it — that is the flicker.
      if (holdVisibleStep) {
        const referenceCommands = [
          CoreEnums.RequestDetailCommandType.Details,
          CoreEnums.RequestDetailCommandType.History,
          CoreEnums.RequestDetailCommandType.Share,
          CoreEnums.RequestDetailCommandType.StartRequest,
        ];
        let changed = false;
        if (markPreviousCompleted && previousActionInstance?.id) {
          const prevIndex = _.findIndex(actionInstances, { id: previousActionInstance.id });
          if (
            prevIndex !== -1 &&
            actionInstances[prevIndex].status !== CoreEnums.ActionInstanceStatus.Finished
          ) {
            actionInstances[prevIndex] = {
              ...actionInstances[prevIndex],
              status: CoreEnums.ActionInstanceStatus.Finished,
              endDate: actionInstances[prevIndex].endDate || new Date().toISOString(),
            };
            changed = true;
          }
        }
        // The step they are looking at just closed. Keep that card, drop the
        // action buttons, and leave share/history available.
        const finishedVisibleStep =
          !!previousActionInstance?.id &&
          previousActionInstance.id === state.currentItem?.currentActionInstanceId;
        const quietCommands = finishedVisibleStep
          ? (state.currentItem?.commands || []).filter((item) =>
              referenceCommands.includes(item.command),
            )
          : state.currentItem?.commands;
        const statusChanged =
          !_.isNil(processInstanceStatus) && processInstanceStatus !== state.currentItem?.status;
        if (!changed && !statusChanged && !finishedVisibleStep) return state;
        return {
          ...state,
          executedCommand: finishedVisibleStep ? null : state.executedCommand,
          currentItem: {
            ...state.currentItem,
            ...(changed && { actionInstances }),
            ...(statusChanged && { status: processInstanceStatus }),
            ...(finishedVisibleStep &&
              state.currentItem?.currentActionInstance && {
                currentActionInstance: {
                  ...state.currentItem.currentActionInstance,
                  status: CoreEnums.ActionInstanceStatus.Finished,
                  endDate:
                    state.currentItem.currentActionInstance.endDate || new Date().toISOString(),
                },
                commands: quietCommands,
              }),
          },
        };
      }

      if (
        approvers &&
        (_.isEqual(approvers, state.currentItem?.processInstanceApprovers) ||
          JSON.stringify(approvers) === JSON.stringify(state.currentItem?.processInstanceApprovers))
      ) {
        return state;
      }

      if (currentActionInstance) {
        const isSameMessage = _.find(
          actionInstances,
          (action) => action.id === currentActionInstance.id,
        );

        if (isSameMessage) {
          if (markPreviousCompleted && previousActionInstance?.id) {
            const prevIndex = _.findIndex(actionInstances, { id: previousActionInstance.id });
            if (prevIndex !== -1) {
              actionInstances[prevIndex] = {
                ...actionInstances[prevIndex],
                status: CoreEnums.ActionInstanceStatus.Finished,
                endDate: new Date().toISOString(),
              };
            }
          }

          return {
            ...state,
            currentItem: {
              ...state.currentItem,
              actionInstances,
            },
          };
        }

        actionInstances.push(currentActionInstance);

        if (markPreviousCompleted && previousActionInstance?.id) {
          const prevIndex = _.findIndex(actionInstances, { id: previousActionInstance.id });
          if (prevIndex !== -1) {
            actionInstances[prevIndex] = {
              ...actionInstances[prevIndex],
              status: CoreEnums.ActionInstanceStatus.Finished,
              endDate: new Date().toISOString(),
            };
          }
        }
      }

      let index = -1;

      if (!approvers && (currentActionInstanceId || actionInstanceId || previousActionInstance)) {
        index = _.findIndex(actionInstances, {
          id: currentActionInstanceId || actionInstanceId || previousActionInstance?.id,
        });

        if (index !== -1) {
          if (
            actionInstances[index].status === actionInstanceStatus ||
            actionInstances[index].status === currentActionInstanceStatus
          ) {
            return state; // .. is same message, ignore
          }

          actionInstances[index] = {
            ...actionInstances[index],
            ...previousActionInstance,
            ...((!_.isNil(actionInstanceStatus) || !_.isNil(currentActionInstanceStatus)) && {
              status: actionInstanceStatus || currentActionInstanceStatus,
            }),
          };
        }
      }

      const stepStatusChanged =
        iterationResultId && state.stepStatusChanged?.status !== (status || stepStatus)
          ? { id: iterationResultId, status: status || stepStatus }
          : null;

      const stepAdvanced =
        !!currentActionInstance?.id &&
        currentActionInstance.id !== state.currentItem?.currentActionInstanceId;

      const listChanged = actionInstances.some(
        (step, index) => step !== state.currentItem?.actionInstances?.[index],
      );
      // Status ticks for a step this screen does not render. A new state
      // object here re-renders the whole detail on every socket message.
      if (
        !listChanged &&
        !approvers &&
        _.isNil(processInstanceStatus) &&
        !currentActionInstance &&
        !stepStatusChanged &&
        !stepAdvanced
      ) {
        return state;
      }

      const newState = {
        ...state,
        // Unlock detail action buttons once the current step actually advances.
        ...(stepAdvanced && { executedCommand: null }),
        stepStatusChanged,
        currentItem: {
          ...state.currentItem,
          ...(approvers && { processInstanceApprovers: approvers }),
          ...(!_.isNil(processInstanceStatus) && { status: processInstanceStatus }),
          ...(currentActionInstance && {
            actionInstances, // Updated array with new action
            currentActionInstance,
            currentActionInstanceId: currentActionInstance.id,
          }),
          // Even if no currentActionInstance, update actionInstances if modified
          ...(!currentActionInstance &&
            actionInstances.length !== state.currentItem?.actionInstances?.length && {
              actionInstances,
            }),
          // Drop previous-step commands and mark them stale until fetchById
          // tags the new step — prevents Approve/Reject flashing on the next card.
          ...(stepAdvanced && {
            commands: [],
            commandsActionInstanceId: null,
          }),
        },
      };

      return newState;
    },

    queryEventTrace(state, { payload }) {
      return {
        ...state,
        emailEventTrace: payload,
      };
    },

    queryCustomActiveTab(state, { payload }) {
      return {
        ...state,
        customActiveTab: payload ?? null,
      };
    },

    queryInstanceActionType(state, { payload }) {
      return {
        ...state,
        instanceActionType: payload ?? false,
      };
    },

    queryActionTypeResult(state, { payload }) {
      return {
        ...state,
        [payload.source]: {
          data: payload?.items ?? payload?.item ?? [],
          pagination: {
            ...state[payload.source]?.pagination,
            ...toPaginate(payload),
          },
        },
      };
    },

    clearActionTypeResult(state, { payload }) {
      const newState = { ...state };
      if (payload?.id) {
        delete newState[payload.id];
      }
      return newState;
    },

    queryEmailEventTraceResult(state, { payload }) {
      return {
        ...state,
        emailEventTraceResult: payload?.items || [],
        emailEventTraceResultPagination: {
          ...state.emailEventTracePagination,
          ...toPaginate(payload),
        },
      };
    },

    resetEmailEventTraceResult(state) {
      return {
        ...state,
        emailEventTraceResult: [],
        emailEventTraceResultPagination: { ...meta, limit: 10 },
      };
    },

    queryAutoOpenEditEnabled(state, { payload }) {
      return {
        ...state,
        autoOpenEditOperation: payload,
      };
    },

    cleanupSelect(state) {
      return {
        ...state,
        selectItem: null,
        ...notices,
      };
    },

    queryCommandSyncPending(state) {
      return {
        ...state,
        commandSyncPending: true,
      };
    },

    cleanupCurrent(state) {
      return {
        ...state,
        currentItem: null,
        selectItem: null,
        formConfig: null,
        completeFormConfig: null,
        continueValidationErrors: [],
        autoOpenEditOperation: false,
        executedCommand: null,
        commandSyncPending: false,
        bulkTaskItemData: [],
        bulkTaskItemPagination: meta,
        ...notices,
      };
    },

    queryCleanup(state, { payload }) {
      if (payload?.source) {
        return {
          ..._.omit(state, [payload.source]),
        };
      }
      return initialState;
    },
  },
};

export default Model;
